// ============================================================================
// parseCnpjPayload — fonte única de verdade para extrair TODOS os campos do
// payload bruto da CNPJ.ws/Receita Federal e formatar para o componente
// ReceitaFederalDetails.
//
// Antes desta unificação havia duas implementações divergentes:
//   - TabReceitaFederal.tsx: parseCache (extraía tudo: capital, Simples/MEI,
//     inscrições estaduais, regimes tributários, motivo situação, etc.)
//   - ReceitaFederalModal.tsx: usava mapearDadosCNPJ (subset, omitia ~10
//     campos que o ReceitaFederalDetails sabia renderizar)
//
// Esta função substitui ambos os caminhos. O payload de entrada é o JSONB
// armazenado em cnpj_cache.payload OU o `result.data` retornado pela edge
// function consultar-cnpj. Schema esperado: CNPJ.ws comercial.
// ============================================================================

/** Safely extract a string from a value that may be an object with descricao/text */
const str = (v: any): string => {
  if (v == null) return "";
  if (typeof v === "object") return v.descricao || v.text || v.nome || v.sigla || "";
  return String(v);
};

const fmtPhone = (ddd: string | null | undefined, num: string | null | undefined) => {
  if (!ddd && !num) return "";
  return `(${ddd || ""}) ${num || ""}`.trim();
};

export interface ParsedCnpj {
  // Cabeçalho
  razaoSocial: string;
  nomeFantasia: string;
  cnpj: string;
  situacaoCadastral: string;

  // Dados Cadastrais
  cnpjRaiz: string;
  tipo: string;
  dataInicioAtividade: string;
  dataSituacaoCadastral: string;
  motivoSituacaoCadastral: string;
  situacaoEspecial: string;
  dataSituacaoEspecial: string;
  portEmpresa: string;
  capitalSocial?: number;
  naturezaJuridica: string;
  qualificacaoResponsavel: string;
  responsavelFederativo: string;

  // Atividades
  cnaeAtividade: string;
  atividadesSecundarias: Array<{ codigo: string; descricao: string }>;

  // Simples / MEI
  simplesOptante: boolean | null;
  simplesDataOpcao: string;
  simplesDataExclusao: string;
  meiOptante: boolean | null;
  meiDataOpcao: string;
  meiDataExclusao: string;

  // Contato
  telefone1: string;
  telefone2: string;
  fax: string;
  email: string;

  // Endereço
  tipoLogradouro: string;
  endereco: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  uf: string;
  cep: string;
  pais: string;
  nomeCidadeExterior: string;

  // Sócios
  socios: Array<{
    nome: string;
    cpf: string;
    qualificacao: string;
    dataEntrada: string;
    isAdministrador: boolean;
    tipo: string;
    faixaEtaria: string;
    pais: string;
  }>;

  // Inscrições e Regimes
  inscricoesEstaduais: Array<{ estado: string; numero: string; ativo: boolean }>;
  regimesTributarios: string[];
}

export function parseCnpjPayload(payload: unknown): ParsedCnpj | null {
  if (!payload) return null;
  const p: any = typeof payload === "string" ? JSON.parse(payload) : payload;
  const est = p.estabelecimento || {};

  // Sócios (suporta tanto socios quanto qsa)
  const socios = (p.socios || p.qsa || []).map((s: any) => {
    const qualStr = str(s.qualificacao_socio || s.qualificacao);
    return {
      nome: s.nome_socio || s.nome || "",
      cpf: s.cpf_cnpj_socio || s.cnpj_cpf_do_socio || s.cpf || "",
      qualificacao: qualStr,
      dataEntrada: s.data_entrada_sociedade || s.data_entrada || "",
      isAdministrador: qualStr.toLowerCase().includes("administrador"),
      tipo: s.tipo || "",
      faixaEtaria: s.faixa_etaria || "",
      pais: str(s.pais),
    };
  });

  // Atividades secundárias
  const atividadesSecundarias = (est.atividades_secundarias || p.atividades_secundarias || []).map(
    (a: any) => ({
      codigo: a.subclasse || a.classe || a.id || "",
      descricao: a.descricao || a.text || "",
    })
  );

  // CNAE principal
  const cnaePrincipal = est.atividade_principal || (Array.isArray(p.atividade_principal) ? p.atividade_principal[0] : null);
  const cnaeDescricao = cnaePrincipal
    ? `${cnaePrincipal.subclasse || cnaePrincipal.classe || cnaePrincipal.id || ""} - ${cnaePrincipal.descricao || cnaePrincipal.text || ""}`.replace(/^\s*-\s*/, "")
    : p.cnae_fiscal_descricao || "";

  // Inscrições estaduais
  const inscricoesEstaduais = (est.inscricoes_estaduais || []).map((ie: any) => ({
    estado: str(ie.estado),
    numero: ie.inscricao_estadual || ie.numero || "",
    ativo: ie.ativo ?? true,
  }));

  // Regimes tributários
  const regimesTributarios = (est.regimes_tributarios || []).map((r: any) => str(r));

  // Simples/MEI
  const simples = p.simples || {};
  const simplesOptante =
    simples.simples === "Sim" || simples.optante === true
      ? true
      : simples.simples === "Não" || simples.optante === false
        ? false
        : null;
  const meiOptante =
    simples.mei === "Sim" || simples.mei_optante === true
      ? true
      : simples.mei === "Não" || simples.mei_optante === false
        ? false
        : null;

  return {
    // Cabeçalho
    razaoSocial: p.razao_social || p.nome || "",
    nomeFantasia: est.nome_fantasia || p.nome_fantasia || p.fantasia || "",
    cnpj: est.cnpj || p.cnpj || "",
    situacaoCadastral:
      est.situacao_cadastral ||
      str(p.situacao_cadastral) ||
      p.descricao_situacao_cadastral ||
      "",

    // Dados Cadastrais
    cnpjRaiz: p.cnpj_raiz || est.cnpj_raiz || "",
    tipo: est.tipo || "",
    dataInicioAtividade: est.data_inicio_atividade || p.data_inicio_atividade || p.abertura || "",
    dataSituacaoCadastral: est.data_situacao_cadastral || "",
    motivoSituacaoCadastral: str(est.motivo_situacao_cadastral),
    situacaoEspecial: est.situacao_especial || "",
    dataSituacaoEspecial: est.data_situacao_especial || "",
    portEmpresa: str(p.porte),
    capitalSocial: p.capital_social ? Number(p.capital_social) : undefined,
    naturezaJuridica: str(p.natureza_juridica),
    qualificacaoResponsavel: str(p.qualificacao_do_responsavel),
    responsavelFederativo: p.responsavel_federativo || "",

    // Atividades
    cnaeAtividade: cnaeDescricao,
    atividadesSecundarias,

    // Simples / MEI
    simplesOptante,
    simplesDataOpcao: simples.data_opcao_simples || simples.data_opcao || "",
    simplesDataExclusao: simples.data_exclusao_simples || simples.data_exclusao || "",
    meiOptante,
    meiDataOpcao: simples.data_opcao_mei || "",
    meiDataExclusao: simples.data_exclusao_mei || "",

    // Contato
    telefone1: fmtPhone(est.ddd1, est.telefone1) || p.telefone || "",
    telefone2: fmtPhone(est.ddd2, est.telefone2) || "",
    fax: fmtPhone(est.ddd_fax, est.fax) || "",
    email: est.email || p.email || "",

    // Endereço
    tipoLogradouro: est.tipo_logradouro || "",
    endereco: est.logradouro || p.logradouro || "",
    numero: est.numero || p.numero || "",
    complemento: est.complemento || p.complemento || "",
    bairro: est.bairro || p.bairro || "",
    cidade: str(est.cidade) || p.municipio || "",
    uf: str(est.estado) || p.uf || "",
    cep: est.cep || p.cep || "",
    pais: str(est.pais) || "",
    nomeCidadeExterior: est.nome_cidade_exterior || "",

    socios,
    inscricoesEstaduais,
    regimesTributarios,
  };
}
